import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * Hook to handle user inactivity with warning before logout
 * @param {Function} onLogout - Function to call when user should be logged out
 * @param {boolean} isAuthenticated - Whether the user is currently authenticated
 * @param {number} idleTimeMinutes - Total idle time in minutes before logout (default: 15)
 * @param {number} warningTimeSeconds - Warning time in seconds before logout (default: 60)
 * @returns {Object} - { showWarning, remainingSeconds, resetTimer }
 */
export default function useIdleWithWarning(
  onLogout,
  isAuthenticated,
  idleTimeMinutes = 15,
  warningTimeSeconds = 60
) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(warningTimeSeconds);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const warningTimerRef = useRef(null);

  const resetTimer = useCallback(() => {
    setShowWarning(false);
    setRemainingSeconds(warningTimeSeconds);

    // Clear countdown and warning timer
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, [warningTimeSeconds]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const idleTimeMs = idleTimeMinutes * 60 * 1000;
    const warningTimeMs = warningTimeSeconds * 1000;
    const timeUntilWarning = idleTimeMs - warningTimeMs;

    const showWarningAndStartCountdown = () => {
      setShowWarning(true);

      // Start countdown
      let seconds = warningTimeSeconds;
      setRemainingSeconds(seconds);

      countdownRef.current = setInterval(() => {
        seconds -= 1;
        setRemainingSeconds(seconds);

        if (seconds <= 0) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
      }, 1000);

      // Set timer for final logout
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(false);
        onLogout();
      }, warningTimeMs);
    };

    const resetIdleTimer = () => {
      // Clear existing idle timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // If warning is showing, reset it
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      setShowWarning(false);
      setRemainingSeconds(warningTimeSeconds);

      // Set new idle timer
      timerRef.current = setTimeout(() => {
        showWarningAndStartCountdown();
      }, timeUntilWarning);
    };

    // Track user activity events
    const events = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    events.forEach(event => {
      window.addEventListener(event, resetIdleTimer, { passive: true });
    });

    // Start initial timer
    resetIdleTimer();

    return () => {
      // Cleanup
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetIdleTimer);
      });
    };
  }, [isAuthenticated, onLogout, idleTimeMinutes, warningTimeSeconds]);

  return { showWarning, remainingSeconds, resetTimer };
}
